// DonationPageInlineFixed.js
import React, { useEffect, useMemo, useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useParams, Link } from "react-router-dom";
import Navbar from "../ui/Navbar";
import Button from "../ui/Button";
import Input from "../ui/Input";
import config from "../../config";
import terms from "../../config/terminology";
import { formatCurrency, formatAmount } from "../../utils/formatCurrency";


// Helper function to convert error objects to strings
const formatErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.detail) {
    if (typeof error.detail === 'string') return error.detail;
    if (Array.isArray(error.detail)) {
      return error.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    }
  }
  return 'An error occurred. Please try again.';
};

export default function DonationPageInlineFixed() {
  const { username } = useParams();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(5);
  const [donorUsername, setDonorUsername] = useState("");
  const [message, setMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [result, setResult] = useState(null);
  const [userNotFound, setUserNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [donatorId, setDonatorId] = useState(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showAccountCreation, setShowAccountCreation] = useState(false);
  const [wantsToCreateAccount, setWantsToCreateAccount] = useState(false);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.API_URL}/users/username/${username}/profile`);
        if (res.ok) {
          const profile = await res.json();
          setUserProfile(profile);

          // Fetch recent donations for this user
          try {
            const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${profile.id}&completed_only=true`);
            if (donationsRes.ok) {
              const donations = await donationsRes.json();
              setRecentDonations(donations);
            }
          } catch (error) {
            console.log("Could not fetch recent donations:", error);
          }
        } else if (res.status === 404) {
          setUserNotFound(true);
        }
      } catch {
        setUserNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [username]);

  // Check for authentication on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.API_URL}/donators/me`, {
          credentials: 'include' // Include cookies
        });
        if (res.ok) {
          const donator = await res.json();
          setDonatorId(donator.id);
          setFirstName(donator.first_name);
          setLastName(donator.last_name);
          setDonorEmail(donator.email);
          console.log("Restored logged-in donator:", donator);
        }
      } catch (error) {
        console.log("No authenticated donator session found");
      }
    })();
  }, []);

  // Fetch saved payment methods when on Step 2 and logged in
  useEffect(() => {
    if (step === 2 && donatorId) {
      (async () => {
        try {
          const res = await fetch(`${config.API_URL}/donators/${donatorId}/payment-methods`, {
            credentials: 'include'
          });
          if (res.ok) {
            const methods = await res.json();
            setSavedPaymentMethods(methods);
            console.log("Loaded saved payment methods:", methods);

            // Auto-select default payment method if available
            const defaultMethod = methods.find(m => m.is_default);
            if (defaultMethod) {
              setSelectedPaymentMethod(defaultMethod.id);
            }
          }
        } catch (error) {
          console.log("Could not load payment methods:", error);
        }
      })();
    }
  }, [step, donatorId]);

  // Poll for new donations at configurable interval
  useEffect(() => {
    if (!userProfile?.id) return;

    let intervalId = null;

    const fetchDonations = async () => {
      try {
        const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${userProfile.id}&completed_only=true`);
        if (donationsRes.ok) {
          const donations = await donationsRes.json();
          setRecentDonations(donations);
        }
      } catch (error) {
        console.log("Could not fetch recent donations:", error);
      }
    };

    const setupPolling = async () => {
      try {
        const res = await fetch(`${config.API_URL}/settings/donation-polling-interval`);
        if (res.ok) {
          const { polling_interval } = await res.json();
          if (polling_interval > 0) {
            intervalId = setInterval(fetchDonations, polling_interval * 1000);
          }
        }
      } catch (error) {
        console.log("Could not fetch polling interval, using default:", error);
        // Default to 10 seconds if we can't fetch the setting
        intervalId = setInterval(fetchDonations, 10000);
      }
    };

    setupPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [userProfile?.id]);

  const platformFee = useMemo(() => Number((0.50 + (amount * 0.05)).toFixed(2)), [amount]);
  const total = useMemo(() => Number((amount + platformFee).toFixed(2)), [amount, platformFee]);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!donorEmail) return setEmailError("Email is required");
    if (!emailRx.test(donorEmail)) return setEmailError("Please enter a valid email");
    setEmailError("");

    setPaymentLoading(true);
    try {
      const res = await fetch(`${config.API_URL}/payments/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amount * 100,
          recipient_id: userProfile?.id,
          donor_name: donorUsername || "Anonymous",
          donor_email: donorEmail,
          message: message || "",
          save_payment_method: savePaymentMethod || wantsToCreateAccount, // Save if logged in user wants to save OR if creating account
          donator_id: donatorId,
        }),
      });
      const data = await res.json();
      if (!data?.client_secret) {
        const errorMsg = typeof data?.detail === 'string' ? data.detail : "Couldn't start the payment. Please try again.";
        setFormError(errorMsg);
        setPaymentLoading(false);
        return;
      }

      let r;
      const donorName = donorUsername || "Anonymous";

      // Use saved payment method if selected
      if (selectedPaymentMethod) {
        console.log("Using saved payment method:", selectedPaymentMethod);
        r = await stripe.confirmCardPayment(data.client_secret, {
          payment_method: selectedPaymentMethod,
        });
      } else {
        // Use new card from CardElement
        const card = elements.getElement(CardElement);
        if (!card) {
          setFormError("Payment form not ready. Please try again.");
          setPaymentLoading(false);
          return;
        }
        r = await stripe.confirmCardPayment(data.client_secret, {
          payment_method: { card, billing_details: { name: donorName, email: donorEmail } },
        });
      }

      if (r.error) setFormError(r.error.message || "Payment failed. Try again.");
      else if (r.paymentIntent?.status === "succeeded") {
        setResult({ amount, transactionId: r.paymentIntent.id, date: new Date().toLocaleString() });

        // Payment method is automatically saved by Stripe when setup_future_usage is set
        if (donatorId && savePaymentMethod) {
          console.log("Payment method will be automatically saved by Stripe for customer");
        }

        // Save payment method ID if user wants to create account
        if (wantsToCreateAccount && !donatorId && r.paymentIntent.payment_method) {
          setSavedPaymentMethod(r.paymentIntent.payment_method);
          console.log("Payment method saved for account creation:", r.paymentIntent.payment_method);
        }

        setStep(3);

        // Refresh donations list after successful payment
        try {
          const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${userProfile.id}&completed_only=true`);
          if (donationsRes.ok) {
            const donations = await donationsRes.json();
            setRecentDonations(donations);
          }
        } catch (error) {
          console.log("Could not refresh recent donations:", error);
        }

        // Don't auto-transition - let user create account or manually go back
      } else setFormError("Payment not completed. Try again.");
    } catch {
      setFormError("Something went wrong.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setDonorUsername("");
    setMessage("");
    // Keep first name, last name, email if user is logged in
    if (!donatorId) {
      setFirstName("");
      setLastName("");
      setDonorEmail("");
    }
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setEmailError("");
    setFormError("");
    setResult(null);
    setAmount(5);
    // Don't clear donatorId - keep user logged in
    // setDonatorId(null);
    setSavePaymentMethod(false);
    setShowAccountCreation(false);
    setAgreedToTerms(false);
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        <div style={{ minHeight: "100vh", backgroundColor: "#14161f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ 
              width: "40px", 
              height: "40px", 
              border: "3px solid #6fb5e0", 
              borderTop: "3px solid transparent", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite",
              margin: "0 auto 1rem"
            }} />
            <p style={{ color: "#9ea7c4" }}>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  // Show blocked page if creator hasn't connected Stripe
  if (userProfile && !userProfile.payments_enabled) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#14161f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: "400px", padding: "2rem" }}>
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#1c2d45",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 2rem",
            fontSize: "2rem"
          }}>
            🏰
          </div>
          <h1 style={{ fontSize: "2rem", fontWeight: "bold", marginBottom: "1rem", color: "#fff" }}>
            Not accepting {terms.donations} yet
          </h1>
          <p style={{ fontSize: "1.125rem", color: "#9ea7c4", marginBottom: "2rem", lineHeight: 1.6 }}>
            {userProfile.username} hasn't set up payments yet. Check back soon!
          </p>
          <Link
            to={`/castle/${username}`}
            style={{
              display: "inline-block",
              padding: "0.75rem 2rem",
              backgroundColor: "#6fb5e0",
              color: "#14161f",
              textDecoration: "none",
              borderRadius: "9999px",
              fontWeight: "600",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#4fa2d4"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "#6fb5e0"}
          >
            View Castle
          </Link>
        </div>
      </div>
    );
  }

  // Show error page if user not found
  if (userNotFound) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#14161f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: "400px", padding: "2rem" }}>
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#c44040",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 2rem",
            fontSize: "2rem"
          }}>
            ❌
          </div>
          <h1 style={{ fontSize: "2rem", fontWeight: "bold", marginBottom: "1rem", color: "#fff" }}>
            Stream Account Not Found
          </h1>
          <p style={{ fontSize: "1.125rem", color: "#9ea7c4", marginBottom: "2rem", lineHeight: 1.6 }}>
            The stream account you're looking for doesn't exist or may have been removed.
          </p>
          <Link
            to="/"
            style={{
              display: "inline-block",
              padding: "0.75rem 2rem",
              backgroundColor: "#6fb5e0",
              color: "#14161f",
              textDecoration: "none",
              borderRadius: "9999px",
              fontWeight: "600",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#4fa2d4"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "#6fb5e0"}
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      <div style={{ minHeight: "100vh", backgroundColor: "#14161f", color: "#fff" }}>
      <Navbar
        creator={{ name: userProfile ? userProfile.username : "", isFounder: userProfile?.is_founder, logo_url: userProfile?.logo_url }}
        user={{ name: donorUsername || (firstName && lastName ? `${firstName} ${lastName}` : null) || "RottingJackal", avatar: null }}
        donator={donatorId ? { name: `${firstName} ${lastName}`, id: donatorId } : null}
        socialLinks={[]}
        onSettingsClick={() => {}}
        onDonatorLogout={() => {
          setDonatorId(null);
          setFirstName("");
          setLastName("");
          setDonorEmail("");
        }}
      />

      <div className="flex min-h-[calc(100vh-60px)] pt-[60px]">
        {/* LEFT */}
        <section style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${window.location.hostname === 'mycastle.gg' || window.location.hostname === 'www.mycastle.gg' ? '/app' : ''}/castle-knight-default-bg.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              opacity: 0.5,
              zIndex: 0,
            }}
          />
          <div style={{ position: "relative", zIndex: 1, padding: "4rem 2rem", maxWidth: 760 }}>
            <h1 style={{ fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.25, marginBottom: "2rem", color: "#6fb5e0", whiteSpace: "pre-line" }}>
              {userProfile?.thank_you_message || `Hey there! I really appreciate the tip. It's never expected, but anything goes towards making my content not suck!`}
            </h1>
            <p style={{ fontSize: "1.25rem", color: "#9ea7c4", marginBottom: "1.5rem" }}>
              Thank you for your support!
            </p>
            <p style={{ fontSize: "1.125rem", color: "#9ea7c4", fontStyle: "italic", marginBottom: "3rem" }}>
              - {userProfile ? userProfile.username : "Average Matty"}
            </p>
            
            {/* Recent Donations */}
            {recentDonations.length > 0 && (
              <div style={{
                background: "rgba(26,29,33,0.8)",
                borderRadius: "1rem",
                padding: "1.5rem",
                border: "1px solid rgba(153, 194, 255, 0.2)"
              }}>
                <h3 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem", color: "#6fb5e0" }}>
                  Recent {terms.Donations}
                </h3>
                <div>
                  {recentDonations.slice(0, 3).map((donation, index) => (
                    <div
                      key={donation.id}
                      style={{
                        padding: "0.75rem",
                        marginBottom: index < Math.min(recentDonations.length, 3) - 1 ? "0.75rem" : 0,
                        background: "rgba(15, 17, 20, 0.5)",
                        borderRadius: "0.5rem",
                        border: "1px solid rgba(255,255,255,0.1)"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: donation.message ? "0.375rem" : 0 }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#6fb5e0" }}>
                          {donation.name || "Anonymous"}
                        </span>
                        <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#10B981" }}>
                          {formatCurrency(donation.amount)}
                        </span>
                      </div>
                      {donation.message && (
                        <p style={{ fontSize: "0.8rem", color: "#9ea7c4", lineHeight: 1.4, marginBottom: "0.375rem", wordWrap: "break-word" }}>
                          "{donation.message}"
                        </p>
                      )}
                      <p style={{ fontSize: "0.7rem", color: "#6a789f" }}>
                        {new Date(donation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* RIGHT */}
        <aside style={{ width: 480, padding: "2rem", position: "relative", zIndex: 1 }}>
          {step === 1 && (
            <div
              style={{
                borderRadius: "1rem",
                background: "rgba(26,29,33,0.9)",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.4)",
                overflow: "hidden",
                animation: step === 1 && !fadeOut ? "fadeIn 0.5s ease-in" : undefined,
              }}
            >
              {/* Amount */}
              <div style={{ padding: "2rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1.5rem", marginBottom: "1rem" }}>
                  <button
                    onClick={() => setAmount(Math.max(1, amount - 5))}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#1a1d2e",
                      border: "1px solid #6fb5e0",
                      color: "#6fb5e0",
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#6fb5e0";
                      e.currentTarget.style.color = "#14161f";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#1a1d2e";
                      e.currentTarget.style.color = "#6fb5e0";
                    }}
                  >
                    −
                  </button>
                  
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={`$${amount}`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value === '') {
                          setAmount(0);
                        } else {
                          const numValue = parseInt(value);
                          setAmount(Math.min(Math.max(1, numValue), 999));
                        }
                      }}
                      onBlur={(e) => {
                        if (amount === 0) {
                          setAmount(1);
                        }
                      }}
                      onClick={(e) => e.target.select()}
                      onFocus={(e) => e.target.select()}
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        border: "3px solid #6fb5e0",
                        background: "#1c1f2d",
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        color: "#fff",
                        textAlign: "center",
                        outline: "none",
                      }}
                    />
                  </div>

                  <button
                    onClick={() => setAmount(Math.min(999, amount + 5))}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#1a1d2e",
                      border: "1px solid #6fb5e0",
                      color: "#6fb5e0",
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#6fb5e0";
                      e.currentTarget.style.color = "#14161f";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#1a1d2e";
                      e.currentTarget.style.color = "#6fb5e0";
                    }}
                  >
                    +
                  </button>
                </div>
                
                <p style={{ textAlign: "center", fontSize: "0.875rem" }}>
                  100% of this amount will be sent to{" "}
                  <span style={{ color: "#6fb5e0" }}>
                    {userProfile ? userProfile.username : "AverageMatty"}
                  </span>
                </p>
                <p style={{ marginTop: "0.5rem", textAlign: "center", fontSize: "0.75rem", color: "#6a789f" }}>
                  A 5% + $0.50 platform fee will be added at checkout.
                </p>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Who's tipping */}
              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>Who's tipping?</h3>
                <Input
                  placeholder="Your name (optional)"
                  value={donorUsername}
                  onChange={(e) => setDonorUsername(e.target.value)}
                  style={{
                    background: "#1a1d2e",
                    color: "#fff",
                    borderRadius: "0.375rem",
                    border: "1px solid #6fb5e0",
                  }}
                />
                <p style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#6a789f" }}>
                  This will be displayed with your tip.
                </p>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Message */}
              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>Leave a message (optional)</h3>
                <textarea
                  placeholder="Say something nice..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    background: "#1a1d2e",
                    color: "#fff",
                    borderRadius: "0.375rem",
                    border: "1px solid #6fb5e0",
                    resize: "none",
                    fontSize: "0.875rem",
                    fontFamily: "inherit"
                  }}
                />
                <p style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#6a789f" }}>
                  {message.length}/500 characters
                </p>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Step dots */}
              <div style={{ padding: "2rem" }}>
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6fb5e0" }} />
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2c3350" }} />
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2c3350" }} />
                </div>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* CTA */}
              <div style={{ padding: "2rem", background: "#1c1f2d" }}>
                <Button
                  onClick={() => setStep(2)}
                  style={{
                    backgroundColor: "#4fa2d4",
                    color: "#14161f",
                    fontWeight: 700,
                    borderRadius: "9999px",
                    width: "100%",
                    padding: "0.75rem",
                    marginBottom: "1rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#3d8fbe")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4fa2d4")}
                >
                  NEXT STEP ➜
                </Button>
                <p style={{ fontSize: "0.625rem", color: "#6a789f", textAlign: "center", lineHeight: 1.4 }}>
                  {terms.DONATIONS} ARE NON-REFUNDABLE
                  <br />
                  See our{" "}
                  <a href="https://www.mycastle.gg/terms-of-service" style={{ color: "#6fb5e0" }} target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div
              style={{
                borderRadius: "1rem",
                background: "rgba(26,29,33,0.9)",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.4)",
                overflow: "hidden",
              }}
            >
              {/* Payment breakdown */}
              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>Payment Details</h3>
                <div style={{ 
                  marginBottom: "1rem", 
                  padding: "1rem", 
                  background: "#1c1f2d", 
                  borderRadius: "0.5rem",
                  border: "1px solid #6fb5e0"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                    <span>{terms.Donation}</span><strong>{formatCurrency(amount)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#9ea7c4", marginBottom: "0.5rem" }}>
                    <span>Platform fee (5% + $0.50)</span><span>{formatCurrency(platformFee)}</span>
                  </div>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "0.5rem 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Total</span><strong>{formatCurrency(total)}</strong>
                  </div>
                </div>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Payment method */}
              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem" }}>Payment method</h3>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
                  <Input
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    style={{
                      background: "#1a1d2e",
                      color: "#fff",
                      borderRadius: "0.375rem",
                      border: "1px solid #6fb5e0",
                    }}
                  />
                  <Input
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    style={{
                      background: "#1a1d2e",
                      color: "#fff",
                      borderRadius: "0.375rem",
                      border: "1px solid #6fb5e0",
                    }}
                  />
                </div>

                <Input
                  type="email"
                  placeholder="Email (required for receipt)"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  style={{
                    marginBottom: "0.75rem",
                    background: "#1a1d2e",
                    color: "#fff",
                    borderRadius: "0.375rem",
                    border: "1px solid #6fb5e0",
                  }}
                  error={emailError}
                />

                {/* Show saved payment methods for logged in users */}
                {donatorId && savedPaymentMethods.length > 0 && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <p style={{ fontSize: "0.875rem", color: "#9ea7c4", marginBottom: "0.5rem" }}>
                      Saved payment methods
                    </p>
                    {savedPaymentMethods.map(method => (
                      <div
                        key={method.id}
                        style={{
                          padding: "0.75rem",
                          background: "#1a1d2e",
                          borderRadius: "0.375rem",
                          border: `1px solid ${selectedPaymentMethod === method.id ? '#4fa2d4' : '#6fb5e0'}`,
                          marginBottom: "0.5rem",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                        onClick={() => {
                          setSelectedPaymentMethod(method.id);
                          setUseNewCard(false);
                        }}
                      >
                        <div>
                          <span style={{ fontSize: "0.875rem", fontWeight: 600, textTransform: "capitalize" }}>
                            {method.brand} ****{method.last4}
                          </span>
                          <span style={{ fontSize: "0.75rem", color: "#9ea7c4", marginLeft: "0.5rem" }}>
                            Exp: {method.exp_month}/{method.exp_year}
                          </span>
                          {method.is_default && (
                            <span style={{ fontSize: "0.7rem", color: "#10B981", marginLeft: "0.5rem", fontWeight: 600 }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        {selectedPaymentMethod === method.id && (
                          <span style={{ color: "#4fa2d4" }}>✓</span>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setSelectedPaymentMethod(null);
                        setUseNewCard(true);
                      }}
                      style={{
                        fontSize: "0.875rem",
                        color: "#4fa2d4",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "0.25rem 0"
                      }}
                    >
                      + Use a different card
                    </button>
                  </div>
                )}

                {/* Show card input if no saved cards or user wants to use new card */}
                {(!donatorId || savedPaymentMethods.length === 0 || useNewCard) && (
                  <div style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    background: "#1a1d2e",
                    borderRadius: "0.375rem",
                    border: "1px solid #6fb5e0"
                  }}>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#ffffff',
                            '::placeholder': {
                              color: '#9ea7c4',
                            },
                          },
                          invalid: {
                            color: '#c44040',
                          },
                        },
                      }}
                    />
                  </div>
                )}

                {/* Show checkbox for logged in users to save payment method (only when entering new card) */}
                {donatorId && (savedPaymentMethods.length === 0 || useNewCard) && (
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    marginBottom: "1rem"
                  }}>
                    <input
                      type="checkbox"
                      checked={savePaymentMethod}
                      onChange={(e) => setSavePaymentMethod(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        accentColor: "#4fa2d4"
                      }}
                    />
                    <span style={{ fontSize: "0.875rem", color: "#9ea7c4" }}>
                      Save this payment method for future {terms.donations}
                    </span>
                  </label>
                )}

                {/* Show checkbox for non-logged in users to create account */}
                {!donatorId && (
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    marginBottom: "1rem"
                  }}>
                    <input
                      type="checkbox"
                      checked={wantsToCreateAccount}
                      onChange={(e) => setWantsToCreateAccount(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        accentColor: "#4fa2d4"
                      }}
                    />
                    <span style={{ fontSize: "0.875rem", color: "#9ea7c4" }}>
                      Create an account to save your info and payment method
                    </span>
                  </label>
                )}

                {formError && <div style={{ color: "#c44040", marginBottom: "1rem", fontSize: "0.875rem" }}>{formError}</div>}
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* CTA */}
              <div style={{ padding: "2rem", background: "#1c1f2d" }}>
                <label style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  cursor: "pointer",
                  marginBottom: "1rem"
                }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#4fa2d4",
                      marginTop: "2px",
                      flexShrink: 0
                    }}
                  />
                  <span style={{ fontSize: "0.625rem", color: "#6a789f", lineHeight: 1.4 }}>
                    {terms.DONATIONS} ARE NON-REFUNDABLE
                    <br />
                    See our{" "}
                    <a href="https://www.mycastle.gg/terms-of-service" style={{ color: "#6fb5e0" }} target="_blank" rel="noopener noreferrer">
                      Terms & Conditions
                    </a>
                  </span>
                </label>
                <Button
                  type="submit"
                  disabled={paymentLoading || !agreedToTerms}
                  onClick={handlePay}
                  style={{
                    backgroundColor: !agreedToTerms ? "#2c3350" : "#4fa2d4",
                    color: "#14161f",
                    fontWeight: 700,
                    borderRadius: "9999px",
                    width: "100%",
                    padding: "0.75rem",
                    marginBottom: "1rem",
                    cursor: !agreedToTerms ? "not-allowed" : "pointer",
                    opacity: !agreedToTerms ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (agreedToTerms && !paymentLoading) {
                      e.currentTarget.style.backgroundColor = "#3d8fbe";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (agreedToTerms && !paymentLoading) {
                      e.currentTarget.style.backgroundColor = "#4fa2d4";
                    }
                  }}
                >
                  {paymentLoading ? "Processing..." : `COMPLETE ${terms.DONATION} ➜`}
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    backgroundColor: "transparent",
                    color: "#6fb5e0",
                    fontWeight: 600,
                    borderRadius: "9999px",
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #6fb5e0",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#6fb5e0";
                    e.currentTarget.style.color = "#14161f";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#6fb5e0";
                  }}
                >
                  ← BACK
                </Button>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div
              style={{
                borderRadius: "1rem",
                background: "rgba(26,29,33,0.9)",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.4)",
                overflow: "hidden",
                opacity: fadeOut ? 0 : 1,
                transition: "opacity 0.5s ease-out",
              }}
            >
              {/* Success message */}
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <div
                  style={{
                    margin: "0 auto 1rem",
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#10B981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: 32 }}>✓</span>
                </div>
                <h2 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "1rem" }}>Thank You!</h2>
                <p style={{ color: "#9ea7c4", marginBottom: "0.5rem" }}>
                  Your {terms.donation} of {formatCurrency(result.amount)} has been sent to {userProfile?.username || "the creator"}.
                </p>
                <p style={{ fontSize: "0.875rem", color: "#6a789f" }}>{result.date}</p>
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Account creation section */}
              {!donatorId ? (
                (!showAccountCreation && !wantsToCreateAccount) ? (
                  <div style={{ padding: "2rem", textAlign: "center" }}>
                    <p style={{ fontSize: "0.875rem", color: "#9ea7c4", marginBottom: "1rem" }}>
                      Want to save your info for faster {terms.donations} next time?
                    </p>
                    <Button
                      onClick={() => setShowAccountCreation(true)}
                      style={{
                        backgroundColor: "#4fa2d4",
                        color: "#14161f",
                        fontWeight: 600,
                        borderRadius: "9999px",
                        padding: "0.75rem 2rem",
                        marginBottom: "1rem",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#3d8fbe")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4fa2d4")}
                    >
                      Create Account
                    </Button>
                  </div>
                ) : (
                  <div style={{ padding: "2rem" }}>
                    <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "1rem", textAlign: "center" }}>
                      Create Your Account
                    </h3>
                    <p style={{ fontSize: "0.875rem", color: "#9ea7c4", marginBottom: "1rem", textAlign: "center" }}>
                      We'll save your info: {firstName || "Anonymous"} ({donorEmail})
                    </p>

                    <Input
                      type="password"
                      placeholder="Password (min 8 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        marginBottom: "0.75rem",
                        background: "#1a1d2e",
                        color: "#fff",
                        borderRadius: "0.375rem",
                        border: "1px solid #6fb5e0",
                      }}
                    />

                    <Input
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        marginBottom: "0.75rem",
                        background: "#1a1d2e",
                        color: "#fff",
                        borderRadius: "0.375rem",
                        border: "1px solid #6fb5e0",
                      }}
                    />

                    {passwordError && (
                      <p style={{ fontSize: "0.75rem", color: "#c44040", marginBottom: "0.75rem" }}>
                        {passwordError}
                      </p>
                    )}

                    {creatingAccount && (
                      <p style={{ fontSize: "0.75rem", color: "#10B981", marginBottom: "0.75rem" }}>
                        Creating your account...
                      </p>
                    )}

                    <Button
                      onClick={async () => {
                        console.log("Creating account with:", { firstName, lastName, donorEmail, password: "***" });

                        if (!firstName || !lastName || !donorEmail) {
                          setPasswordError("Please go back and fill in your first name, last name, and email");
                          return;
                        }

                        if (password.length < 8) {
                          setPasswordError("Password must be at least 8 characters");
                          return;
                        }

                        if (password !== confirmPassword) {
                          setPasswordError("Passwords don't match");
                          return;
                        }

                        setPasswordError("");
                        setCreatingAccount(true);

                        try {
                          console.log("Attempting to create donator account...");
                          const createRes = await fetch(`${config.API_URL}/donators/`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              first_name: firstName,
                              last_name: lastName,
                              email: donorEmail,
                              password: password,
                            }),
                          });

                          const createData = await createRes.json();
                          console.log("Create response:", createRes.status, createData);

                          if (createRes.ok) {
                            console.log("Account created successfully, attempting login...");

                            // Auto-login after account creation
                            const loginRes = await fetch(`${config.API_URL}/donators/login`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: 'include', // CRITICAL: Include cookies so the cookie is stored
                              body: JSON.stringify({
                                email: donorEmail,
                                password: password,
                              }),
                            });

                            const loginData = await loginRes.json();
                            console.log("Login response:", loginRes.status, loginData);

                            if (loginRes.ok) {
                              setDonatorId(loginData.donator.id);
                              console.log("Account created and logged in! Donator ID:", loginData.donator.id);

                              // Attach saved payment method if available
                              if (savedPaymentMethod) {
                                try {
                                  console.log("Attaching saved payment method:", savedPaymentMethod);
                                  const attachRes = await fetch(`${config.API_URL}/donators/${loginData.donator.id}/payment-methods`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: 'include',
                                    body: JSON.stringify({
                                      payment_method_id: savedPaymentMethod
                                    }),
                                  });

                                  if (attachRes.ok) {
                                    console.log("Payment method attached to account successfully!");
                                  } else {
                                    console.error("Failed to attach payment method:", await attachRes.json());
                                  }
                                } catch (error) {
                                  console.error("Error attaching payment method:", error);
                                }
                              }
                            } else {
                              setDonatorId(createData.id);
                              console.log("Login failed, but account created with ID:", createData.id);
                            }
                          } else {
                            console.error("Account creation failed:", createData);
                            setPasswordError(formatErrorMessage(createData));
                          }
                        } catch (error) {
                          console.error("Error creating account:", error);
                          setPasswordError("Something went wrong. Please try again.");
                        } finally {
                          setCreatingAccount(false);
                        }
                      }}
                      disabled={creatingAccount}
                      style={{
                        backgroundColor: "#4fa2d4",
                        color: "#14161f",
                        fontWeight: 600,
                        borderRadius: "9999px",
                        width: "100%",
                        padding: "0.75rem",
                        marginBottom: "0.75rem",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#3d8fbe")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4fa2d4")}
                    >
                      {creatingAccount ? "Creating..." : "Create Account"}
                    </Button>

                    <Button
                      onClick={() => setShowAccountCreation(false)}
                      style={{
                        backgroundColor: "transparent",
                        color: "#6fb5e0",
                        fontWeight: 600,
                        borderRadius: "9999px",
                        width: "100%",
                        padding: "0.75rem",
                        border: "1px solid #6fb5e0",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#6fb5e0";
                        e.currentTarget.style.color = "#14161f";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#6fb5e0";
                      }}
                    >
                      Skip
                    </Button>
                  </div>
                )
              ) : (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <div style={{
                    padding: "1rem",
                    background: "#1c1f2d",
                    borderRadius: "0.5rem",
                    border: "1px solid #10B981",
                    marginBottom: "1rem"
                  }}>
                    <p style={{ fontSize: "0.875rem", color: "#10B981", marginBottom: "0.5rem" }}>
                      ✓ Account created successfully!
                    </p>
                    <p style={{ fontSize: "0.75rem", color: "#9ea7c4" }}>
                      You're now logged in and your info is saved for future {terms.donations}.
                    </p>
                  </div>
                </div>
              )}

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

              {/* Back to donation button */}
              <div style={{ padding: "2rem", background: "#1c1f2d" }}>
                <Button
                  onClick={() => {
                    setFadeOut(true);
                    setTimeout(() => {
                      reset();
                      setFadeOut(false);
                    }, 500);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    color: "#6fb5e0",
                    fontWeight: 600,
                    borderRadius: "9999px",
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #6fb5e0",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#6fb5e0";
                    e.currentTarget.style.color = "#14161f";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#6fb5e0";
                  }}
                >
                  ← Make Another {terms.Donation}
                </Button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
    </>
  );
}
